import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { shift } from '@floating-ui/react';
import es from 'date-fns/locale/es';
import { format } from 'date-fns';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import './styles.css';

registerLocale('es', es);

const MONTH_LABELS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const useIsMobileCalendar = () => {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia('(max-width: 767px)').matches;
    });

    useEffect(() => {
        const media = window.matchMedia('(max-width: 767px)');
        const handleChange = (event) => setIsMobile(event.matches);
        handleChange(media);
        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
    }, []);

    return isMobile;
};

const buildYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 80;
    const endYear = currentYear + 10;
    return Array.from(
        { length: endYear - startYear + 1 },
        (_, index) => endYear - index,
    );
};

const YEAR_OPTIONS = buildYearOptions();

const normalizeDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const safeFormatDate = (value, pattern) => {
    const date = normalizeDate(value);
    if (!date) return '';
    try {
        return format(date, pattern, { locale: es });
    } catch {
        return '';
    }
};

export const InputDateModern = ({
    id,
    value,
    onChange,
    label,
    placeholder = 'Selecciona una fecha',
    minDate = null,
    maxDate = null,
    disabled = false,
    error = null,
    required = false,
    /** Activa selector de rango (inicio/fin) con dos meses visibles. */
    selectsRange = false,
    startDate = null,
    endDate = null,
    /** Ej. `top-start` abre el calendario arriba del campo (evita solaparse con botones debajo). */
    popperPlacement = 'bottom-start',
}) => {
    const [touched, setTouched] = useState(false);
    const [isPopperReady, setIsPopperReady] = useState(false);
    const isMobile = useIsMobileCalendar();

    const handleCalendarOpen = useCallback(() => {
        setIsPopperReady(false);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setIsPopperReady(true));
        });
    }, []);

    const handleCalendarClose = useCallback(() => {
        setIsPopperReady(false);
    }, []);

    const renderPopperContainer = useCallback(({ children }) => {
        if (typeof document === 'undefined') return children;
        return createPortal(children, document.body);
    }, []);

    const normalizedValue = normalizeDate(value);
    const normalizedStartDate = normalizeDate(startDate);
    const normalizedEndDate = normalizeDate(endDate);

    const hasRangeValue = Boolean(normalizedStartDate && normalizedEndDate);
    const hasSingleValue = Boolean(normalizedValue);
    const isEmpty = selectsRange
        ? !hasRangeValue && !(normalizedStartDate || normalizedEndDate)
        : !hasSingleValue;

    const internalError =
        required && touched && isEmpty && !error ? 'Este campo es requerido' : null;
    const mergedError = error || internalError;

    const handleChange = (nextValue) => {
        if (selectsRange) {
            const range = Array.isArray(nextValue) ? nextValue : [nextValue, null];
            onChange?.(range);
            return;
        }
        onChange?.(nextValue);
    };

    const rangePlaceholder = placeholder === 'Selecciona una fecha'
        ? 'Selecciona inicio y fin…'
        : placeholder;

    const renderCalendarHeader = ({
        monthDate,
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        customHeaderCount = 0,
    }) => {
        const headerDate = monthDate || date;
        const isFirstMonth = customHeaderCount === 0;
        const isLastMonth = customHeaderCount === monthsShown - 1;
        const showPrevNav = monthsShown === 1 ? true : isFirstMonth;
        const showNextNav = monthsShown === 1 ? true : isLastMonth;

        return (
        <div className="cfamex-calendar-header">
            {showPrevNav ? (
            <button
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                type="button"
                aria-label="Mes anterior"
                className="cfamex-calendar-header__nav"
            >
                <svg className="cfamex-calendar-header__nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            </button>
            ) : (
                <span className="cfamex-calendar-header__nav cfamex-calendar-header__nav--spacer" aria-hidden="true" />
            )}

            <div className="cfamex-calendar-header__selects">
                <select
                    value={headerDate.getMonth()}
                    onChange={({ target: { value } }) => changeMonth(parseInt(value, 10))}
                    aria-label="Mes"
                    className="cfamex-calendar-header__select"
                >
                    {MONTH_LABELS.map((month, index) => (
                        <option key={month} value={index}>
                            {month}
                        </option>
                    ))}
                </select>

                <select
                    value={headerDate.getFullYear()}
                    onChange={({ target: { value } }) => changeYear(parseInt(value, 10))}
                    aria-label="Año"
                    className="cfamex-calendar-header__select cfamex-calendar-header__select--year"
                >
                    {YEAR_OPTIONS.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>

            {showNextNav ? (
            <button
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                type="button"
                aria-label="Mes siguiente"
                className="cfamex-calendar-header__nav"
            >
                <svg className="cfamex-calendar-header__nav-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </button>
            ) : (
                <span className="cfamex-calendar-header__nav cfamex-calendar-header__nav--spacer" aria-hidden="true" />
            )}
        </div>
        );
    };

    // En móvil: un solo mes (el doble mes no cabe en drawers estrechos).
    const monthsShown = selectsRange ? (isMobile ? 1 : 2) : 1;
    const isDualMonthRange = selectsRange && monthsShown > 1;

    const popperClassNames = [
        'cfamex-datepicker-popper',
        isDualMonthRange ? 'cfamex-datepicker-popper--range' : '',
        isMobile ? 'cfamex-datepicker-popper--mobile' : '',
        isPopperReady ? 'cfamex-datepicker-popper--ready' : '',
    ].filter(Boolean).join(' ');

    // react-datepicker v6 usa Floating UI: popperModifiers deben ser middleware, no objetos de Popper.js.
    const popperModifiers = useMemo(
        () => [shift({ padding: 8, crossAxis: true })],
        [],
    );

    return (
        <div className="w-full">
            {label && (
                <label
                    htmlFor={id}
                    className="block text-md font-semibold text-gray-900 leading-6 ml-1"
                >
                    {label}
                    {required && (
                        <span className="text-red-500 ml-1" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
            )}
            <div className={label ? 'mt-3' : ''}>
            <DatePicker
                id={id}
                selected={selectsRange ? normalizedStartDate : normalizedValue}
                startDate={selectsRange ? normalizedStartDate : undefined}
                endDate={selectsRange ? normalizedEndDate : undefined}
                onChange={handleChange}
                selectsRange={selectsRange}
                monthsShown={monthsShown}
                locale="es"
                dateFormat="dd 'de' MMMM 'de' yyyy"
                placeholderText={selectsRange ? rangePlaceholder : placeholder}
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
                showPopperArrow={false}
                popperPlacement={isMobile ? 'bottom' : popperPlacement}
                popperModifiers={popperModifiers}
                popperClassName={popperClassNames}
                popperContainer={renderPopperContainer}
                popperProps={{ strategy: 'fixed' }}
                onCalendarOpen={handleCalendarOpen}
                onCalendarClose={handleCalendarClose}
                calendarClassName={selectsRange ? 'modern-calendar modern-calendar-range' : 'modern-calendar'}
                openToDate={selectsRange ? (normalizedStartDate || normalizedEndDate || new Date()) : undefined}
                renderCustomHeader={renderCalendarHeader}
                shouldCloseOnSelect={!selectsRange}
                dayClassName={(date) => {
                    const day = date.getDay();
                    return day === 0 || day === 6 ? 'cfamex-calendar-day--weekend' : undefined;
                }}
                customInput={
                    <CustomInput
                        id={id}
                        error={mergedError}
                        onBlurField={() => setTouched(true)}
                        ariaRequired={required}
                        displayValue={selectsRange ? formatRangeLabel(normalizedStartDate, normalizedEndDate) : undefined}
                    />
                }
            />
            </div>
            {mergedError && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1 ml-1">
                    <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {mergedError}
                </p>
            )}
        </div>
    );
};

const formatRangeLabel = (startDate, endDate) => {
    const start = safeFormatDate(startDate, "dd 'de' MMMM 'de' yyyy");
    const end = safeFormatDate(endDate, "dd 'de' MMMM 'de' yyyy");
    if (start && end) return `${start} – ${end}`;
    if (start) return `${start} – …`;
    return '';
};

const CustomInput = forwardRef(({
    id,
    value,
    onClick,
    placeholder,
    error,
    onBlurField,
    ariaRequired,
    displayValue,
}, ref) => {
    const hasValue = Boolean(displayValue || value);

    return (
        <div className="date-input-modern" onClick={onClick}>
            <div
                className={`date-input-modern__field${error ? ' date-input-modern__field--error' : ''}`}
            >
                <div className="date-input-modern__icon-box" aria-hidden="true">
                    <CalendarDaysIcon />
                </div>
                <input
                    ref={ref}
                    id={id}
                    type="text"
                    value={displayValue ?? value ?? ''}
                    placeholder={placeholder}
                    readOnly
                    onBlur={onBlurField}
                    aria-invalid={error ? true : undefined}
                    aria-required={ariaRequired || undefined}
                    className={`date-input-modern__input${hasValue ? ' date-input-modern__input--filled' : ''}`}
                />
            </div>
        </div>
    );
});

CustomInput.displayName = 'CustomInput';
